// The global spoken-voice player — a module-level singleton that guarantees the
// AI never talks over itself. EVERY voice playback (auto-speak and manual
// per-card buttons, across every session controller) goes through this one
// object, so there is structurally only ever a single utterance in flight.
//
// It also owns the "cut" semantics: a new play() cancels whatever is currently
// playing OR still synthesizing. Because each play() takes a producer thunk
// (the async TTS fetch), a generation guard discards a stale blob that finishes
// synthesizing after a newer play() has already superseded it — so clicking
// card A then card B never ends with A's audio sneaking in after B starts.

import { play as playAudio, type Utterance } from '@/lib/ai/tts'

interface Active {
  gen: number
  /** What's being spoken — a turn id, so the UI can show which card is live. */
  token: string
  /** null while still synthesizing (the floor is claimed but no audio yet). */
  utt: Utterance | null
}

let active: Active | null = null
let gen = 0
const listeners = new Set<() => void>()

function notify(): void {
  for (const fn of listeners) fn()
}

export interface VoicePlayer {
  /** Play `token`'s audio, cutting whatever is currently playing/synthesizing.
   *  `produce` is the (cancellable) TTS fetch; if a newer play() lands before it
   *  resolves, its blob is discarded. Resolves when playback finishes or is cut. */
  play(
    token: string,
    produce: () => Promise<Blob | null>,
    outputDeviceId?: string
  ): Promise<void>
  /** Stop whatever is playing (and invalidate any in-flight synthesis). */
  stop(): void
  /** The token currently playing/synthesizing, or null. */
  current(): string | null
  /** Subscribe to play/stop transitions (for a "now speaking" indicator). */
  subscribe(fn: () => void): () => void
}

export const voicePlayer: VoicePlayer = {
  async play(token, produce, outputDeviceId) {
    const myGen = ++gen
    // Claim the floor synchronously (before the first await) so a caller that
    // checks current() right after kicking us off sees us immediately — this is
    // what stops two near-simultaneous auto-plays from both starting.
    active?.utt?.stop()
    active = { gen: myGen, token, utt: null }
    notify()

    let blob: Blob | null = null
    try {
      blob = await produce()
    } catch {
      blob = null
    }
    // A newer play()/stop() superseded us while synthesizing — drop this blob.
    if (gen !== myGen) return
    if (!blob) {
      active = null
      notify()
      return
    }

    const utt = playAudio(blob, outputDeviceId)
    active = { gen: myGen, token, utt }
    notify()
    await utt.done
    if (active?.gen === myGen) {
      active = null
      notify()
    }
  },

  stop() {
    if (!active) return
    gen++ // invalidate any in-flight synthesis so it can't start playing
    active.utt?.stop()
    active = null
    notify()
  },

  current() {
    return active?.token ?? null
  },

  subscribe(fn) {
    listeners.add(fn)
    return () => {
      listeners.delete(fn)
    }
  }
}
